import { useState, useEffect, useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Bed,
  Bath,
  Ruler,
  TreePine,
  Camera,
  ExternalLink,
  AlertCircle,
  RefreshCw,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface IDXListingsGridProps {
  savedLinkId: string;
  communityName: string;
}

type SortOption = "price-desc" | "price-asc" | "newest" | "most-photos";

interface IDXListing {
  listingID?: string;
  idxID?: string;
  address?: string;
  cityName?: string;
  state?: string;
  zipcode?: string;
  bedrooms?: string;
  fullBaths?: string;
  partialBaths?: string;
  sqFt?: string;
  acres?: string;
  remarksConcat?: string;
  fullDetailsURL?: string;
  idxStatus?: string;
  priceData?: {
    value?: {
      formatted?: string;
      raw?: number;
    };
  };
  image?: {
    [key: string]: {
      url?: string;
    } | number;
    totalCount?: number;
  };
  listingDate?: string;
}

const IDXListingsGrid = ({ savedLinkId, communityName }: IDXListingsGridProps) => {
  const [listings, setListings] = useState<IDXListing[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sort, setSort] = useState<SortOption>("price-desc");

  const fetchListings = async () => {
    setLoading(true);
    setError(null);
    try {
      const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
      const anonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
      const res = await fetch(
        `https://${projectId}.supabase.co/functions/v1/idx-savedlink-results?savedLinkId=${savedLinkId}`,
        {
          headers: {
            apikey: anonKey,
            Authorization: `Bearer ${anonKey}`,
          },
        }
      );

      if (!res.ok) {
        throw new Error(`Failed to fetch listings (${res.status})`);
      }

      const json = await res.json();

      // IDX Broker returns an object with numeric keys for each listing
      const listingArray: IDXListing[] = [];
      if (json && typeof json === "object") {
        for (const key of Object.keys(json)) {
          if (/^\d+$/.test(key) && typeof json[key] === "object") {
            listingArray.push(json[key]);
          }
        }
      }

      setListings(listingArray);
    } catch (err: unknown) {
      console.error("Fetch listings error:", err);
      setError(err instanceof Error ? err.message : "Failed to load listings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchListings();
  }, [savedLinkId]);

  const sorted = useMemo(() => {
    const copy = [...listings];
    switch (sort) {
      case "price-desc":
        return copy.sort(
          (a, b) => (b.priceData?.value?.raw ?? 0) - (a.priceData?.value?.raw ?? 0)
        );
      case "price-asc":
        return copy.sort(
          (a, b) => (a.priceData?.value?.raw ?? 0) - (b.priceData?.value?.raw ?? 0)
        );
      case "newest":
        return copy.sort(
          (a, b) =>
            new Date(b.listingDate ?? 0).getTime() -
            new Date(a.listingDate ?? 0).getTime()
        );
      case "most-photos":
        return copy.sort(
          (a, b) =>
            (typeof b.image?.totalCount === "number" ? b.image.totalCount : 0) -
            (typeof a.image?.totalCount === "number" ? a.image.totalCount : 0)
        );
      default:
        return copy;
    }
  }, [listings, sort]);

  if (loading) {
    return (
      <div>
        <div className="flex items-center justify-between mb-6">
          <Skeleton className="h-8 w-72" />
          <Skeleton className="h-10 w-48" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <Card key={i} className="overflow-hidden">
              <Skeleton className="h-52 w-full" />
              <CardContent className="pt-4 space-y-3">
                <Skeleton className="h-6 w-32" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-10 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive" className="max-w-xl mx-auto">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Unable to Load Listings</AlertTitle>
        <AlertDescription className="mt-2">
          <p className="mb-3">{error}</p>
          <Button variant="outline" size="sm" onClick={fetchListings} className="gap-2">
            <RefreshCw className="h-4 w-4" />
            Try Again
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  const getImageUrl = (listing: IDXListing): string | null => {
    if (listing.image && listing.image["0"] && typeof listing.image["0"] === "object") {
      return (listing.image["0"] as { url?: string }).url || null;
    }
    return null;
  };

  const getPhotoCount = (listing: IDXListing): number => {
    if (listing.image && typeof listing.image.totalCount === "number") {
      return listing.image.totalCount;
    }
    return 0;
  };

  const getStatusColor = (status?: string) => {
    const s = (status || "").toUpperCase();
    if (s === "ACTIVE") return "bg-green-600 text-white";
    if (s.includes("COMING")) return "bg-blue-600 text-white";
    return "bg-muted text-muted-foreground";
  };

  const truncate = (text?: string, max = 150) => {
    if (!text) return "";
    return text.length > max ? text.slice(0, max) + "..." : text;
  };

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <h3 className="text-xl font-heading font-semibold text-foreground">
          {sorted.length} Homes for Sale in {communityName}, NJ
        </h3>
        <Select value={sort} onValueChange={(v) => setSort(v as SortOption)}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Sort by" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="price-desc">Price: High to Low</SelectItem>
            <SelectItem value="price-asc">Price: Low to High</SelectItem>
            <SelectItem value="newest">Newest First</SelectItem>
            <SelectItem value="most-photos">Most Photos</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {sorted.map((listing, index) => {
          const imageUrl = getImageUrl(listing);
          const photoCount = getPhotoCount(listing);
          const partialBaths = parseInt(listing.partialBaths || "0", 10);
          const acresNum = parseFloat(listing.acres || "0");

          return (
            <Card
              key={listing.listingID || index}
              className="overflow-hidden hover:shadow-lg transition-shadow duration-200 group"
            >
              {/* Photo */}
              <div className="relative h-52 bg-muted overflow-hidden">
                {imageUrl ? (
                  <img
                    src={imageUrl}
                    alt={`${listing.address}, ${listing.cityName}`}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                ) : (
                  <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
                    No Photo Available
                  </div>
                )}

                {/* Status badge */}
                {listing.idxStatus && (
                  <Badge
                    className={`absolute top-3 left-3 ${getStatusColor(listing.idxStatus)} border-0 text-xs font-semibold`}
                  >
                    {listing.idxStatus}
                  </Badge>
                )}

                {/* Photo count */}
                {photoCount > 0 && (
                  <div className="absolute bottom-3 right-3 bg-black/70 text-white text-xs px-2 py-1 rounded-md flex items-center gap-1">
                    <Camera className="h-3 w-3" />
                    {photoCount}
                  </div>
                )}
              </div>

              <CardContent className="pt-4 space-y-3">
                {/* Price */}
                <p className="text-2xl font-bold" style={{ color: "#D5153C" }}>
                  {listing.priceData?.value?.formatted || "Price N/A"}
                </p>

                {/* Address */}
                <p className="text-sm text-foreground font-medium leading-snug">
                  {listing.address}
                  <br />
                  <span className="text-muted-foreground">
                    {listing.cityName}, {listing.state} {listing.zipcode}
                  </span>
                </p>

                {/* Stats */}
                <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                  {listing.bedrooms && (
                    <span className="flex items-center gap-1">
                      <Bed className="h-4 w-4" /> {listing.bedrooms} Beds
                    </span>
                  )}
                  {listing.fullBaths && (
                    <span className="flex items-center gap-1">
                      <Bath className="h-4 w-4" /> {listing.fullBaths} Bath
                      {partialBaths > 0 && (
                        <span className="text-xs ml-0.5">+ {partialBaths} half</span>
                      )}
                    </span>
                  )}
                  {listing.sqFt && (
                    <span className="flex items-center gap-1">
                      <Ruler className="h-4 w-4" /> {Number(listing.sqFt.replace(/,/g, '')).toLocaleString()} sqft
                    </span>
                  )}
                  {acresNum > 0 && (
                    <span className="flex items-center gap-1">
                      <TreePine className="h-4 w-4" /> {listing.acres} ac
                    </span>
                  )}
                </div>

                {/* Description */}
                {listing.remarksConcat && (
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {truncate(listing.remarksConcat)}
                  </p>
                )}

                {/* CTA */}
                {listing.fullDetailsURL && (
                  <Button
                    asChild
                    className="w-full gap-2"
                    style={{ backgroundColor: "#D5153C" }}
                  >
                    <a
                      href={listing.fullDetailsURL}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View Listing
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  </Button>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* MLS Compliance Footer */}
      <div className="text-xs text-gray-400 mt-6 pt-4 border-t border-gray-100 flex flex-col items-center gap-2 text-center">
        <img
          src="https://s3.amazonaws.com/staticos.idxbroker.com/mls-logos/b004-logoURL2-idxsandbox"
          alt="Bright MLS Logo"
          height={24}
          style={{ height: 24 }}
          className="object-contain"
        />
        <p>
          © 2026 Bright MLS. All rights reserved. Information Deemed Reliable, but Not Guaranteed. Listing information is from various brokers who participate in IDX and not all listings may be visible on the site. The property information being provided is for consumers personal, non-commercial use and may not be used for any purpose other than to identify prospective properties consumers may be interested in purchasing. Data services provided by IDX Broker.
        </p>
      </div>
    </div>
  );
};

export default IDXListingsGrid;
